import { useEffect, useRef, useState } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Upload, X, CheckCircle2 } from "lucide-react";

const ANIMALS = ["Dog", "Cat", "Rabbit", "Bird", "Other"];

export default function Consult() {
  const [form, setForm] = useState({
    owner_name: "", owner_phone: "", owner_email: "",
    pet_name: "", animal_type: "Dog", animal_type_other: "",
    breed: "", age: "", sex: "Male",
    reason: "", problem_start: "",
    prior_treatment: "No", prior_treatment_details: "",
    file_ids: [], confirmed_accurate: false,
  });
  const [uploads, setUploads] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [emergencyNotice, setEmergencyNotice] = useState("");
  const fileInput = useRef();

  useEffect(() => { api.get("/settings").then((r) => setEmergencyNotice(r.data.emergency_notice)); }, []);

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try {
        const { data } = await api.post("/consultations/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setUploads((u) => [...u, data]);
        setForm((s) => ({ ...s, file_ids: [...s.file_ids, data.file_id] }));
        toast.success(`Uploaded ${f.name}`);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    if (fileInput.current) fileInput.current.value = "";
  };

  const removeFile = (id) => {
    setUploads((u) => u.filter((f) => f.file_id !== id));
    setForm((s) => ({ ...s, file_ids: s.file_ids.filter((x) => x !== id) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.confirmed_accurate) return toast.error("Please confirm the information is accurate.");
    setSubmitting(true);
    try {
      await api.post("/consultations", form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen ankara" data-testid="consult-thank-you">
        <Header />
        <div className="max-w-xl mx-auto px-4 py-24 text-center">
          <div className="h-16 w-16 rounded-full bg-[#0F766E]/15 flex items-center justify-center mx-auto"><CheckCircle2 size={32} className="text-[#0F766E]" /></div>
          <h1 className="font-display text-3xl mt-6">Thank you</h1>
          <p className="mt-4 text-[#57534E]">Your veterinary consultation request has been received. Jay's Veterinary Consult and Services will review your request and contact you using the phone number provided.</p>
          <a href="/" className="mt-8 inline-block btn-primary px-6 py-3 rounded-full text-sm font-semibold">Back to home</a>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen ankara" data-testid="consult-page">
      <Header />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-24">
        <div className="chip chip-terra mb-3">Consultation</div>
        <h1 className="font-display text-3xl md:text-5xl leading-[1.05]">Veterinary Consultation Form</h1>
        <p className="mt-4 text-[#57534E]">Share your pet's health concern and Dr. Juwon will contact you.</p>

        {/* Emergency notice */}
        <div className="mt-6 flex gap-3 p-4 rounded-2xl bg-[#FFF1E0] border-2 border-[#C2410C]/40" data-testid="emergency-notice">
          <AlertTriangle size={20} className="text-[#C2410C] shrink-0 mt-0.5" />
          <p className="text-sm text-[#7C2D12]">{emergencyNotice}</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-8" data-testid="consultation-form">
          {/* Owner */}
          <Section title="Owner Information">
            <Field label="Owner's Name" required>
              <input required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="c-owner-name" className={INPUT} />
            </Field>
            <Field label="Phone Number" required>
              <input required type="tel" value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} data-testid="c-owner-phone" className={INPUT} />
            </Field>
            <Field label="Email (optional)">
              <input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="c-owner-email" className={INPUT} />
            </Field>
          </Section>

          {/* Pet */}
          <Section title="Pet Information">
            <Field label="Pet's Name" required>
              <input required value={form.pet_name} onChange={(e) => setForm({ ...form, pet_name: e.target.value })} data-testid="c-pet-name" className={INPUT} />
            </Field>
            <Field label="Animal Type" required>
              <select value={form.animal_type} onChange={(e) => setForm({ ...form, animal_type: e.target.value })} data-testid="c-animal-type" className={INPUT}>
                {ANIMALS.map((a) => <option key={a}>{a}</option>)}
              </select>
            </Field>
            {form.animal_type === "Other" && (
              <Field label="Specify animal type" required>
                <input required value={form.animal_type_other} onChange={(e) => setForm({ ...form, animal_type_other: e.target.value })} data-testid="c-animal-other" className={INPUT} />
              </Field>
            )}
            <Field label="Breed">
              <input value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} data-testid="c-breed" className={INPUT} />
            </Field>
            <Field label="Age">
              <input placeholder="e.g. 2 years" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="c-age" className={INPUT} />
            </Field>
            <Field label="Sex" required>
              <div className="flex gap-3">
                {["Male", "Female"].map((s) => (
                  <button type="button" key={s} onClick={() => setForm({ ...form, sex: s })} data-testid={`c-sex-${s}`}
                    className={`flex-1 py-2.5 rounded-full text-sm font-medium border transition-colors ${form.sex === s ? "bg-[#0F766E] text-white border-[#0F766E]" : "bg-white text-[#1C1917] border-[#EAD9B0] hover:border-[#0F766E]"}`}>{s}</button>
                ))}
              </div>
            </Field>
          </Section>

          {/* Consult */}
          <Section title="Consultation Details">
            <Field label="Reason for Consultation" required>
              <textarea required rows={4} placeholder="Please describe your pet's health concern or the reason you are requesting a consultation."
                value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="c-reason" className={INPUT} />
            </Field>
            <Field label="When did the problem start?">
              <textarea rows={2} value={form.problem_start} onChange={(e) => setForm({ ...form, problem_start: e.target.value })} data-testid="c-start" className={INPUT} />
            </Field>
            <Field label="Has your pet received any treatment?" required>
              <div className="flex gap-3">
                {["Yes", "No"].map((s) => (
                  <button type="button" key={s} onClick={() => setForm({ ...form, prior_treatment: s })} data-testid={`c-prior-${s}`}
                    className={`flex-1 py-2.5 rounded-full text-sm font-medium border transition-colors ${form.prior_treatment === s ? "bg-[#0F766E] text-white border-[#0F766E]" : "bg-white text-[#1C1917] border-[#EAD9B0] hover:border-[#0F766E]"}`}>{s}</button>
                ))}
              </div>
            </Field>
            {form.prior_treatment === "Yes" && (
              <Field label="If yes, please specify">
                <textarea rows={3} value={form.prior_treatment_details} onChange={(e) => setForm({ ...form, prior_treatment_details: e.target.value })} data-testid="c-prior-details" className={INPUT} />
              </Field>
            )}
          </Section>

          {/* Media */}
          <Section title="Upload Photos or Videos (Optional)">
            <p className="text-xs text-[#57534E]">Supported: JPG, PNG, WEBP, MP4, MOV, WEBM · Max 25 MB per file. Files are stored privately.</p>
            <input ref={fileInput} type="file" multiple accept="image/*,video/*" onChange={handleFile} className="hidden" data-testid="c-file-input" />
            <button type="button" onClick={() => fileInput.current?.click()} className="mt-2 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-white border border-[#EAD9B0] hover:border-[#0F766E]" data-testid="c-upload-btn">
              <Upload size={14} /> Choose files
            </button>
            {uploads.length > 0 && (
              <ul className="mt-3 space-y-2">
                {uploads.map((u) => (
                  <li key={u.file_id} className="flex items-center justify-between p-2 bg-white border border-[#EAD9B0] rounded-lg text-xs">
                    <span className="truncate">{u.filename} <span className="text-[#57534E]">({(u.size/1024/1024).toFixed(2)} MB)</span></span>
                    <button type="button" onClick={() => removeFile(u.file_id)} data-testid={`c-remove-${u.file_id}`}><X size={14} /></button>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Confirmation */}
          <label className="flex items-start gap-3 p-4 bg-white border border-[#EAD9B0] rounded-2xl cursor-pointer">
            <input type="checkbox" checked={form.confirmed_accurate} onChange={(e) => setForm({ ...form, confirmed_accurate: e.target.checked })} data-testid="c-confirm" className="mt-1 h-4 w-4 accent-[#0F766E]" />
            <span className="text-sm">I confirm that the information provided is accurate.</span>
          </label>

          <button type="submit" disabled={submitting || !form.confirmed_accurate}
            className="btn-primary w-full py-4 rounded-full text-base font-semibold disabled:opacity-60" data-testid="c-submit">
            {submitting ? "Submitting…" : "Submit Consultation"}
          </button>
        </form>
      </section>
      <Footer />
    </div>
  );
}

const INPUT = "w-full rounded-lg border border-[#EAD9B0] bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]";

function Section({ title, children }) {
  return (
    <div className="bg-white/60 border border-[#EAD9B0] rounded-3xl p-6">
      <h2 className="font-display text-xl mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#57534E]">{label}{required && <span className="text-[#C2410C]">*</span>}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
